import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { apiClient } from '@/services/api';
import type { AppConfig, BrandingConfig } from '@/types';
import { DEFAULT_CONFIG } from '@/types/config';

interface ConfigContextValue {
  config: AppConfig;
  isLoading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
  updateConfig: (updates: Partial<AppConfig>) => Promise<void>;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

// LocalStorage key for caching config
const CONFIG_CACHE_KEY = 'portal_config_cache';
const CONFIG_CACHE_TIMESTAMP_KEY = 'portal_config_timestamp';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCachedConfig(): AppConfig | null {
  try {
    const cached = localStorage.getItem(CONFIG_CACHE_KEY);
    const timestamp = localStorage.getItem(CONFIG_CACHE_TIMESTAMP_KEY);

    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age < CACHE_TTL_MS) {
        return JSON.parse(cached);
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

function setCachedConfig(config: AppConfig): void {
  try {
    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(config));
    localStorage.setItem(CONFIG_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    // Ignore cache errors
  }
}

export function ConfigProvider({ children }: { children: ReactNode }) {
  // Initialize with cached config or defaults
  const [config, setConfig] = useState<AppConfig>(() => {
    const cached = getCachedConfig();
    return cached || DEFAULT_CONFIG;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch public config (branding) on mount - no auth required
  useEffect(() => {
    const fetchPublicConfig = async () => {
      try {
        const brandingConfig = await apiClient.getPublicConfig();
        setConfig((prev) => ({
          ...prev,
          ...brandingConfig,
        }));
        // Update cache with branding
        setCachedConfig({
          ...config,
          ...brandingConfig,
        });
      } catch (err) {
        // Silently fail - we have defaults
        console.warn('Failed to fetch public config, using defaults:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPublicConfig();
  }, []);

  // Dynamically update favicon when config changes
  useEffect(() => {
    if (config.favicon_url) {
      // Add cache-busting parameter to force browser to reload favicon
      const faviconUrl = config.favicon_url.includes('?')
        ? `${config.favicon_url}&_t=${Date.now()}`
        : `${config.favicon_url}?_t=${Date.now()}`;

      // Determine MIME type based on file extension
      const getMimeType = (url: string): string => {
        const lowerUrl = url.toLowerCase().split('?')[0]; // Remove query params
        if (lowerUrl.endsWith('.ico')) return 'image/x-icon';
        if (lowerUrl.endsWith('.png')) return 'image/png';
        if (lowerUrl.endsWith('.svg')) return 'image/svg+xml';
        if (lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg')) return 'image/jpeg';
        if (lowerUrl.endsWith('.gif')) return 'image/gif';
        return 'image/x-icon'; // Default fallback
      };

      const mimeType = getMimeType(config.favicon_url);

      // Remove all existing favicons first
      const existingLinks = document.querySelectorAll("link[rel*='icon']");
      existingLinks.forEach(link => link.remove());

      // Create new favicon link
      const newLink = document.createElement('link');
      newLink.rel = 'icon';
      newLink.type = mimeType;
      newLink.href = faviconUrl;
      document.head.appendChild(newLink);

      // Also add shortcut icon for broader browser support
      const shortcutLink = document.createElement('link');
      shortcutLink.rel = 'shortcut icon';
      shortcutLink.type = mimeType;
      shortcutLink.href = faviconUrl;
      document.head.appendChild(shortcutLink);
    }
  }, [config.favicon_url]);

  // Update document title when portal name changes
  useEffect(() => {
    if (config.portal_name) {
      document.title = config.portal_name;
    }
  }, [config.portal_name]);

  // Refresh full config (requires auth)
  const refreshConfig = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fullConfig = await apiClient.getConfig();
      setConfig(fullConfig);
      setCachedConfig(fullConfig);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch configuration';
      setError(message);
      // Don't clear existing config on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update config
  const updateConfig = useCallback(async (updates: Partial<AppConfig>) => {
    try {
      const updatedConfig = await apiClient.updateConfig(updates);
      setConfig(updatedConfig);
      setCachedConfig(updatedConfig);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update configuration';
      throw new Error(message);
    }
  }, []);

  return (
    <ConfigContext.Provider
      value={{
        config,
        isLoading,
        error,
        refreshConfig,
        updateConfig,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

// Hook to get just the branding config (commonly used in Layout)
export function useBrandingConfig(): BrandingConfig {
  const { config } = useConfig();
  return {
    logo_light_url: config.logo_light_url,
    logo_dark_url: config.logo_dark_url,
    logo_mark_light_url: config.logo_mark_light_url,
    logo_mark_dark_url: config.logo_mark_dark_url,
    logo_width: config.logo_width,
    favicon_url: config.favicon_url,
  };
}
